import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchProfile = useCallback(async (accessToken) => {
        localStorage.setItem("flow_token", accessToken);
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.error("fetchProfile failed:", e?.response?.status, e?.response?.data || e?.message);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // onAuthStateChange fires INITIAL_SESSION on mount — no separate getSession() needed.
        // Calling both simultaneously causes "body stream already read" in Supabase v2.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            try {
                if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
                    if (session?.access_token) {
                        fetchProfile(session.access_token);
                    } else {
                        setLoading(false);
                    }
                } else if (event === "TOKEN_REFRESHED") {
                    if (session?.access_token) localStorage.setItem("flow_token", session.access_token);
                } else if (event === "SIGNED_OUT") {
                    localStorage.removeItem("flow_token");
                    setUser(null);
                    setLoading(false);
                }
            } catch (e) {
                console.error("Auth state change error:", e);
                setLoading(false);
            }
        });

        return () => subscription.unsubscribe();
    }, [fetchProfile]);

    const login = async (email, password) => {
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw new Error(error.message || "Sign in failed");
        } catch (e) {
            throw new Error(e.message || "Sign in failed");
        }
    };

    const signup = async ({ email, password, name, currency = "USD" }) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { full_name: name, currency } },
        });
        if (error) throw new Error(error.message);
        return data;
    };

    const loginWithGoogle = async () => {
        // redirectTo must land on a real SPA route, not "/" — "/" is now the static
        // marketing landing page served directly by nginx (frontend/public/landing.html),
        // which has no Supabase client wired up to pick up the OAuth callback tokens.
        // "/dashboard" boots the actual app, whose onAuthStateChange listener in this
        // file handles the SIGNED_IN event from the redirect.
        const { error } = await supabase.auth.signInWithOAuth({
            provider: "google",
            options: { redirectTo: `${window.location.origin}/dashboard` },
        });
        if (error) throw new Error(error.message);
    };

    const resetPassword = async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw new Error(error.message);
    };

    const logout = async () => {
        await supabase.auth.signOut();
        localStorage.removeItem("flow_token");
        setUser(null);
        window.location.href = "/login";
    };

    const updateProfile = async (payload) => {
        const { data } = await api.patch("/auth/me", payload);
        setUser(data);
        return data;
    };

    // No real payment processor wired up — see lib/plans.js and CLAUDE.md's Plans
    // section. Sets the plan directly via POST /billing/upgrade.
    const upgradePlan = async (plan) => {
        const { data } = await api.post("/billing/upgrade", { plan });
        setUser(data);
        return data;
    };

    const deleteAccount = async () => {
        await api.delete("/auth/me");
        await supabase.auth.signOut();
        localStorage.removeItem("flow_token");
        setUser(null);
        window.location.href = "/login";
    };

    const refreshMe = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) await fetchProfile(session.access_token);
    }, [fetchProfile]);

    return (
        <AuthCtx.Provider value={{
            user, loading,
            login, signup, loginWithGoogle, resetPassword,
            logout, updateProfile, upgradePlan, deleteAccount, refreshMe,
        }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
